/**
 * Plain-English verification outcomes for Overview “Alerts raised by services”.
 *
 * Invariant: each service resolves outcomes; Overview shows error/warning
 * (and recovery info after prior fails). Success and quiet empty stay silent.
 * New terminal statuses belong in the service summary/resolver — not Overview ifs.
 */
import type {
  ApplicationDeviceRiskResponseDto,
  ApplicationJourneyHeaderDto,
  ApplicationVerificationSnapshotErrorDto,
} from '../api/admin';
import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';
import { declineReasonPlain, plainIncomeFailReason, summarizeLastActionResult } from './applicationJourneyCopy';
import { looksLikeApprovalStatusLabel } from './decisionEngineDeclineCopy';
import { mapLoanReviewToViewModel } from './loanReview/mapLoanReviewToViewModel';
import { buildDeviceTabNarrative } from './applicationTabNarratives';
import { presentIdentityAttempts } from './identityAttemptPresentation';
import { resolveIdentitySessionSummary } from './identitySessionSummary';
import {
  hasMilestone as hasEmploymentMilestone,
  isEmploymentForwardedUnderReview,
} from './employment/employmentJourneyStatus';

export type OverviewOutcomeTone = 'error' | 'warning' | 'info' | 'success';

export type ServiceOutcome = {
  id: string;
  service: string;
  tone: OverviewOutcomeTone;
  title: string;
  detail: string;
  attempt?: number;
};

function readListItems(data: unknown): Record<string, unknown>[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const raw = (data as { items?: unknown }).items;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x),
  );
}

function stringFailReasons(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .map((x) => x.trim());
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function strField(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function identitySessionKey(row: IdentityLookupRow, fallback: string): string {
  return strField(row.iv_verification_id) || strField(row.internal_id) || fallback;
}

function identityAttemptNumber(row: IdentityLookupRow, chronologicalIndex: number): number {
  const raw = row.verification_attempt_number;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.floor(raw);
  return chronologicalIndex + 1;
}

function detailFromSummary(title: string, body: string, fallback: string): string {
  const t = title.trim();
  const b = body.trim();
  if (t && b && !b.toLowerCase().includes(t.toLowerCase())) return `${t}. ${b}`;
  return b || t || fallback;
}

/** Prefer the attempt that holds Match neighborhood primary hit (same idea as Identity tab). */
export function pickIdentityRowForOverviewAlerts(
  rows: IdentityLookupRow[] | null | undefined,
): IdentityLookupRow | null {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;
  return (
    list.find((r) => r.latestDedup?.isDuplicate === true) ??
    list.find((r) => Boolean(strField(r.latestDedup?.matched_hit_customer?.ccid))) ??
    list.find((r) => Boolean(strField(asRecord(r.latestDedup?.candidate_match)?.ccid))) ??
    list.find((r) => (r.latestDedup?.closest_customers?.length ?? 0) > 0) ??
    list.find((r) => {
      const s = String(r.session_status ?? '').toUpperCase();
      return (
        s === 'PENDING_EXISTING_ACCOUNT_LINK' ||
        s === 'LINKED_PENDING_AUTH' ||
        s === 'REJECTED_DUPLICATE'
      );
    }) ??
    list[0] ??
    null
  );
}

/**
 * Journey snapshot sessions use `status` (list API). Lookup uses `session_status`.
 * Coerce list items so Overview can alert from the same source as the Identity tab dot.
 */
export function coerceIdentityRowsFromSessions(
  sessions: unknown | null | undefined,
): IdentityLookupRow[] {
  const items = readListItems(sessions);
  return items.map((item, index) => {
    const status = String(item.status ?? item.session_status ?? '').trim();
    const id =
      strField(item.iv_verification_id) ||
      strField(item.internal_id) ||
      `session-${index}`;
    return {
      iv_verification_id: id,
      internal_id: strField(item.internal_id) || id,
      ccid: strField(item.ccid),
      verification_attempt_number:
        typeof item.verification_attempt_number === 'number'
          ? item.verification_attempt_number
          : null,
      vendor: strField(item.vendor) || 'unknown',
      verification_route: strField(item.verification_route),
      session_status: status,
      email: strField(item.email),
      phone_number: strField(item.phone_number),
      document_type: strField(item.document_type),
      issuing_country: strField(item.issuing_country),
      issuing_province: strField(item.issuing_province),
      expiry_date: strField(item.expiry_date),
      issued_date: strField(item.issued_date),
      verification_date: strField(item.verification_date),
      ocr_data: asRecord(item.ocr_data),
      checks_result: asRecord(item.checks_result),
      created_at: strField(item.created_at) || '',
      updated_at: strField(item.updated_at) || strField(item.created_at) || '',
      mitekCalled: Boolean(item.mitekCalled),
      dossier: null,
      evidenceProcessingPrimary: null,
      latestDedup: null,
      customer: null,
      uploadedImages: [],
    } as IdentityLookupRow;
  });
}

/** Prefer rich lookup rows; fill gaps / status from journey sessions (tab-dot source). */
export function mergeIdentityRowsForOverviewAlerts(
  lookupRows: IdentityLookupRow[] | null | undefined,
  identitySessions?: unknown | null,
): IdentityLookupRow[] {
  const lookup = Array.isArray(lookupRows) ? lookupRows : [];
  const fromSessions = coerceIdentityRowsFromSessions(identitySessions);
  if (lookup.length === 0) return fromSessions;
  if (fromSessions.length === 0) return lookup;

  const byId = new Map<string, IdentityLookupRow>();
  for (const row of lookup) {
    byId.set(identitySessionKey(row, row.iv_verification_id), row);
  }
  for (const session of fromSessions) {
    const key = identitySessionKey(session, session.iv_verification_id);
    const existing = byId.get(key);
    if (!existing) {
      byId.set(key, session);
      continue;
    }
    if (!String(existing.session_status ?? '').trim() && session.session_status) {
      byId.set(key, { ...existing, session_status: session.session_status });
    }
  }
  return [...byId.values()];
}

export function resolveApplicationOutcomes(
  application: ApplicationJourneyHeaderDto,
): ServiceOutcome[] {
  const status = application.status.trim().toUpperCase();
  const declinePlain = declineReasonPlain(application.declineReason);
  if (status !== 'DECLINED' || !declinePlain) return [];
  return [
    {
      id: 'application-declined',
      service: 'Application',
      tone: 'error',
      title: 'Application declined',
      detail: declinePlain,
    },
  ];
}

export function resolveDecisionOutcomes(
  application: ApplicationJourneyHeaderDto,
): ServiceOutcome[] {
  const de = application.decisionEngine;
  const deDecision = String(de?.decision ?? '').toUpperCase();
  const declinePlain = declineReasonPlain(application.declineReason);
  const deExplainRaw =
    (typeof de?.declinedExplanation === 'string' && de.declinedExplanation.trim()) ||
    (typeof de?.declinedReason === 'string' && de.declinedReason.trim()) ||
    null;
  const deHardStop =
    deDecision === 'DECLINE' || deDecision === 'ERROR' || deDecision === 'FAIL';
  if (deHardStop) {
    const deExplain =
      deExplainRaw && !looksLikeApprovalStatusLabel(deExplainRaw) ? deExplainRaw : null;
    return [
      {
        id: 'decision-engine',
        service: 'Decision engine',
        tone: 'error',
        title:
          deDecision === 'ERROR' || deDecision === 'FAIL'
            ? 'Credit decision could not finish'
            : 'Credit decision declined',
        detail: deExplain ?? declinePlain ?? 'Decision engine stopped this application.',
      },
    ];
  }
  if (/REVIEW|MANUAL|PENDING/.test(deDecision)) {
    return [
      {
        id: 'decision-engine-review',
        service: 'Decision engine',
        tone: 'warning',
        title: 'Credit decision needs review',
        detail:
          deExplainRaw && !looksLikeApprovalStatusLabel(deExplainRaw)
            ? deExplainRaw
            : 'Decision engine left this application for a closer look.',
      },
    ];
  }
  return [];
}

export function resolveDeviceOutcomes(
  deviceRisk: ApplicationDeviceRiskResponseDto | null | undefined,
): ServiceOutcome[] {
  if (!deviceRisk) return [];
  const deviceNarrative = buildDeviceTabNarrative(deviceRisk);
  if (deviceNarrative.tone === 'error' || deviceNarrative.tone === 'warning') {
    const detailParts = [deviceNarrative.headline, ...(deviceNarrative.bullets ?? [])]
      .map((s) => s.trim())
      .filter(Boolean);
    return [
      {
        id: 'device-intelligence',
        service: 'Device intelligence',
        tone: deviceNarrative.tone,
        title:
          deviceNarrative.tone === 'error'
            ? 'Device blocked or high risk'
            : 'Device needs review',
        detail: detailParts.join(' '),
      },
    ];
  }
  if (deviceRisk.deviceRiskReview) {
    return [
      {
        id: 'device-intelligence-flag',
        service: 'Device intelligence',
        tone: 'warning',
        title: 'Device flagged for review',
        detail:
          deviceNarrative.headline ||
          'Device intelligence marked this application for review.',
      },
    ];
  }
  return [];
}

/**
 * One outcome per Identity attempt from resolveIdentitySessionSummary.
 * Error/warning always surface; recovery info when a later attempt clears a hard fail.
 */
export function resolveIdentityOutcomes(input: {
  identityRows?: IdentityLookupRow[] | null;
  identityRow?: IdentityLookupRow | null;
  identitySessions?: unknown | null;
  declined?: boolean;
  declinePlain?: string | null;
  declineRaw?: string;
}): ServiceOutcome[] {
  const rows = mergeIdentityRowsForOverviewAlerts(
    Array.isArray(input.identityRows) && input.identityRows.length > 0
      ? input.identityRows
      : input.identityRow
        ? [input.identityRow]
        : [],
    input.identitySessions,
  );

  const attempts = presentIdentityAttempts(rows);
  const chronological = [...attempts].sort(
    (a, b) => new Date(a.row.created_at).getTime() - new Date(b.row.created_at).getTime(),
  );

  const out: ServiceOutcome[] = [];
  let hadError = false;

  for (let i = 0; i < chronological.length; i++) {
    const attempt = chronological[i]!;
    const summary = resolveIdentitySessionSummary(attempt.row);
    const n = identityAttemptNumber(attempt.row, i);
    const key = identitySessionKey(attempt.row, String(n));
    const multi = chronological.length > 1 || n > 1;

    if (summary.tone === 'error') {
      hadError = true;
      out.push({
        id: `identity-fail-${key}`,
        service: 'Identity',
        tone: 'error',
        title: multi ? `Identity attempt ${n} failed` : 'Identity verification failed',
        detail: detailFromSummary(
          summary.title,
          summary.body,
          'This identity verification attempt did not pass.',
        ),
        attempt: n,
      });
      continue;
    }

    if (summary.tone === 'warning') {
      out.push({
        id: `identity-review-${key}`,
        service: 'Identity',
        tone: 'warning',
        title: multi ? `Identity attempt ${n} needs review` : 'Identity needs review',
        detail: detailFromSummary(
          summary.title,
          summary.body,
          'Identity verification needs a closer look.',
        ),
        attempt: n,
      });
    }
  }

  const latest = chronological[chronological.length - 1];
  if (latest && hadError) {
    const latestIndex = chronological.length - 1;
    const latestN = identityAttemptNumber(latest.row, latestIndex);
    const latestSummary = resolveIdentitySessionSummary(latest.row);
    // Recovery only when latest cleared the hard fail and was not already a warning outcome.
    if (latestSummary.tone === 'success' || latestSummary.tone === 'info') {
      const key = identitySessionKey(latest.row, String(latestN));
      out.push({
        id: `identity-recovered-${key}`,
        service: 'Identity',
        tone: 'info',
        title: `Customer verified on attempt ${latestN}`,
        detail:
          latestSummary.body ||
          'A later identity attempt passed after earlier failures.',
        attempt: latestN,
      });
    }
  }

  if (
    out.length === 0 &&
    input.declined &&
    /IDENTITY|DUPLICATE/i.test(input.declineRaw ?? '')
  ) {
    out.push({
      id: 'identity-declined',
      service: 'Identity',
      tone: 'error',
      title: 'Identity verification stopped',
      detail: input.declinePlain ?? 'Identity checks did not pass.',
    });
  }

  return out;
}

export function resolveIncomeOutcomes(input: {
  incomeRecords?: unknown | null;
  lastActionResult?: Record<string, unknown> | null;
}): ServiceOutcome[] {
  const incomeItems = readListItems(input.incomeRecords);
  const out: ServiceOutcome[] = [];

  const incomeFailFromRecords = incomeItems.flatMap((row) => {
    const metrics =
      row.metrics && typeof row.metrics === 'object' && !Array.isArray(row.metrics)
        ? (row.metrics as Record<string, unknown>)
        : {};
    return [
      ...stringFailReasons(row.failReasons),
      ...stringFailReasons(metrics.failReasons),
    ];
  });
  const lastActionIncome = summarizeLastActionResult(input.lastActionResult ?? undefined);
  const incomeFails = [
    ...new Set([
      ...incomeFailFromRecords.map((r) => plainIncomeFailReason(r, { pointToIncomeTab: false })),
      ...(lastActionIncome && /income|bank|flinks|payor|deposit/i.test(lastActionIncome)
        ? [plainIncomeFailReason(lastActionIncome, { pointToIncomeTab: false })]
        : []),
    ]),
  ].filter(Boolean);

  for (const [i, line] of incomeFails.slice(0, 4).entries()) {
    out.push({
      id: `income-${i}`,
      service: 'Income',
      tone: 'warning',
      title: i === 0 ? 'Income verification flag' : 'Additional income flag',
      detail: line,
    });
  }

  if (incomeFails.length === 0 && incomeItems[0]) {
    const incomeStatus = String(
      incomeItems[0].finalStatus ?? incomeItems[0].status ?? incomeItems[0].verdict ?? '',
    ).toUpperCase();
    if (/FAIL|REJECT|DECLIN|DENIED|ERROR/.test(incomeStatus)) {
      out.push({
        id: 'income-status-fail',
        service: 'Income',
        tone: 'error',
        title: 'Income verification did not pass',
        detail: 'Bank income checks did not clear. Open the Income tab for details.',
      });
    } else if (/REVIEW|WARN|MANUAL|PENDING/.test(incomeStatus)) {
      out.push({
        id: 'income-status-review',
        service: 'Income',
        tone: 'warning',
        title: 'Income needs review',
        detail: 'Bank income results need a closer look before they can clear.',
      });
    }
  }

  return out;
}

function parseReviewBundle(raw: unknown): {
  blocking: Array<{ message?: string; status?: string; gate?: string }>;
  advisory: Array<{ message?: string; status?: string; gate?: string }>;
} {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { blocking: [], advisory: [] };
  }
  const o = raw as Record<string, unknown>;
  const blocking = Array.isArray(o.blockingSignals) ? o.blockingSignals : [];
  const advisory = Array.isArray(o.advisorySignals) ? o.advisorySignals : [];
  return {
    blocking: blocking.filter(
      (x): x is Record<string, unknown> => x != null && typeof x === 'object',
    ),
    advisory: advisory.filter(
      (x): x is Record<string, unknown> => x != null && typeof x === 'object',
    ),
  };
}

function footprintPersistedDetail(input: {
  employerFootprintAdminMessage?: string | null;
  employerFootprintPaystubsReason?: string | null;
  fallback: string;
}): string {
  return (
    (typeof input.employerFootprintAdminMessage === 'string' &&
    input.employerFootprintAdminMessage.trim()
      ? input.employerFootprintAdminMessage.trim()
      : null) ||
    (typeof input.employerFootprintPaystubsReason === 'string' &&
    input.employerFootprintPaystubsReason.trim()
      ? input.employerFootprintPaystubsReason.trim()
      : null) ||
    input.fallback
  );
}

function footprintPaystubsRequiredTitle(reason: string): string {
  if (reason === 'EMPLOYER_FOOTPRINT_LOW_CONFIDENCE') {
    return 'Low employer confidence';
  }
  return 'Employer digital footprint — paystubs required';
}

function footprintPaystubsRequiredDetail(
  reason: string,
  adminMessage: string | null,
): string {
  const paystubLine =
    reason === 'EMPLOYER_FOOTPRINT_LOW_CONFIDENCE'
      ? 'Paystubs are required for additional verification.'
      : 'Paystubs are required to continue employer verification.';
  if (adminMessage && !paystubLine.toLowerCase().includes(adminMessage.toLowerCase())) {
    return `${paystubLine} ${adminMessage}`;
  }
  return paystubLine;
}

export function resolveEmployerOutcomes(input: {
  application: ApplicationJourneyHeaderDto;
  employerRecords?: unknown | null;
  employerFootprintStatus?: string | null;
  employerFootprintAdminMessage?: string | null;
  employerFootprintPaystubsReason?: string | null;
}): ServiceOutcome[] {
  const out: ServiceOutcome[] = [];
  const footprint = String(input.employerFootprintStatus ?? '').toUpperCase();
  const paystubsReason =
    typeof input.employerFootprintPaystubsReason === 'string'
      ? input.employerFootprintPaystubsReason.trim()
      : '';
  const adminMessage =
    typeof input.employerFootprintAdminMessage === 'string' &&
    input.employerFootprintAdminMessage.trim()
      ? input.employerFootprintAdminMessage.trim()
      : null;

  if (footprint === 'FAILED') {
    out.push({
      id: 'employer-footprint-failed',
      service: 'Employer',
      tone: 'warning',
      title: 'Digital footprint failed',
      detail: footprintPersistedDetail({
        employerFootprintAdminMessage: input.employerFootprintAdminMessage,
        employerFootprintPaystubsReason: input.employerFootprintPaystubsReason,
        fallback:
          'Salary / purchase footprint could not be completed. Check the Employer tab.',
      }),
    });
  } else if (footprint === 'READY' && paystubsReason) {
    out.push({
      id: 'employer-footprint-paystubs-required',
      service: 'Employer',
      tone: 'warning',
      title: footprintPaystubsRequiredTitle(paystubsReason),
      detail: footprintPaystubsRequiredDetail(paystubsReason, adminMessage),
    });
  }

  const milestones = input.application.completedMilestones ?? [];
  if (
    milestones.includes('EMPLOYMENT_PENDING_REVIEW') ||
    isEmploymentForwardedUnderReview({
      milestones,
      lastActionResult: input.application.lastActionResult,
    })
  ) {
    out.push({
      id: 'employer-pending-review',
      service: 'Employer',
      tone: 'warning',
      title: 'Employment under review',
      detail: 'Employer verification needs a closer look before it can clear.',
    });
  }

  const employerItems = readListItems(input.employerRecords);
  if (employerItems[0] && !hasEmploymentMilestone(milestones, 'EMPLOYMENT_VERIFIED')) {
    const empStatus = String(
      employerItems[0].finalStatus ?? employerItems[0].status ?? employerItems[0].verdict ?? '',
    ).toUpperCase();
    const empFail =
      stringFailReasons(employerItems[0].failReasons)[0] ??
      (typeof employerItems[0].failReason === 'string'
        ? employerItems[0].failReason.trim()
        : '');
    if (/FAIL|REJECT|DECLIN|DENIED|ERROR/.test(empStatus)) {
      out.push({
        id: 'employer-status-fail',
        service: 'Employer',
        tone: 'error',
        title: 'Employer verification did not pass',
        detail:
          empFail || 'Employment checks did not clear. Open the Employer tab for gate details.',
      });
    } else if (/REVIEW|WARN|MANUAL/.test(empStatus)) {
      out.push({
        id: 'employer-status-review',
        service: 'Employer',
        tone: 'warning',
        title: 'Employer verification needs review',
        detail: empFail || 'Employment checks need a closer look before they can clear.',
      });
    }
  }

  const empLatest = input.application.employmentReviewSignals?.latest?.bundle;
  const { blocking, advisory } = parseReviewBundle(empLatest);
  for (const [i, signal] of blocking.slice(0, 4).entries()) {
    const message = typeof signal.message === 'string' ? signal.message.trim() : '';
    if (!message) continue;
    const fail = String(signal.status ?? '').toUpperCase() === 'FAIL';
    out.push({
      id: `employer-blocking-${i}`,
      service: 'Employer',
      tone: fail ? 'error' : 'warning',
      title: fail ? 'Employer check failed' : 'Employer review signal',
      detail: message,
    });
  }
  for (const [i, signal] of advisory.slice(0, 2).entries()) {
    const message = typeof signal.message === 'string' ? signal.message.trim() : '';
    if (!message) continue;
    out.push({
      id: `employer-advisory-${i}`,
      service: 'Employer',
      tone: 'info',
      title: 'Employer advisory',
      detail: message,
    });
  }

  return out;
}

export function resolveLoanReviewOutcomes(
  application: ApplicationJourneyHeaderDto,
): ServiceOutcome[] {
  const out: ServiceOutcome[] = [];
  const loanVm = mapLoanReviewToViewModel(application.loanReview ?? null);
  if (loanVm.decision === 'DECLINE' || loanVm.outcomeLabel === 'Failed') {
    out.push({
      id: 'loan-review-decline',
      service: 'Loan review',
      tone: 'error',
      title: loanVm.outcomeLabel === 'Failed' ? 'Loan review failed' : 'Loan review declined',
      detail: loanVm.primaryReasonPlain || loanVm.businessSummary || 'Loan review did not pass.',
    });
  } else if (loanVm.decision === 'REVIEW' || loanVm.outcomeLabel === 'Needs review') {
    out.push({
      id: 'loan-review-human',
      service: 'Loan review',
      tone: 'warning',
      title: 'Loan review needs a human',
      detail: loanVm.primaryReasonPlain || loanVm.businessSummary || 'Flagged for manual review.',
    });
  }
  for (const [i, note] of [
    ...loanVm.reviewReasonNotes.map((n) => n.text),
    ...loanVm.attention,
  ]
    .filter(Boolean)
    .slice(0, 4)
    .entries()) {
    out.push({
      id: `loan-review-reason-${i}`,
      service: 'Loan review',
      tone: 'warning',
      title: 'Loan review flag',
      detail: note,
    });
  }
  for (const [i, note] of loanVm.alsoNotedNotes
    .map((n) => n.text)
    .filter(Boolean)
    .slice(0, 3)
    .entries()) {
    out.push({
      id: `loan-review-also-${i}`,
      service: 'Loan review',
      tone: 'info',
      title: 'Also noted',
      detail: note,
    });
  }
  for (const [i, note] of loanVm.softFlagNotes
    .map((n) => n.text)
    .filter(Boolean)
    .slice(0, 3)
    .entries()) {
    out.push({
      id: `loan-review-soft-${i}`,
      service: 'Loan review',
      tone: 'info',
      title: 'Soft flag',
      detail: note,
    });
  }
  return out;
}

export function resolveSnapshotErrorOutcomes(
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[] | undefined,
): ServiceOutcome[] {
  return (snapshotErrors ?? []).slice(0, 2).map((err) => {
    const service =
      err.scope === 'identity'
        ? 'Identity'
        : err.scope === 'income'
          ? 'Income'
          : err.scope === 'employer'
            ? 'Employer'
            : 'Application';
    return {
      id: `snapshot-${err.scope}`,
      service,
      tone: 'info' as const,
      title: 'Service data incomplete',
      detail: err.message,
    };
  });
}

/** Outcomes Overview should show: error/warning always; info only for recovery / soft flags / snapshot. */
export function shouldShowOutcomeOnOverview(outcome: ServiceOutcome): boolean {
  if (outcome.tone === 'error' || outcome.tone === 'warning') return true;
  if (outcome.tone === 'info') return true; // recovery, soft flags, snapshot incompleteness
  return false;
}
